<template>
  <div class="study-page">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <h1 class="page-title">📖 课程学习</h1>
    </div>

    <!-- 视频播放器 -->
    <div class="video-container" :class="{ 'video-loading': !videoReady }">
      <!-- 骨架屏/加载状态 -->
      <div class="video-skeleton" v-if="!videoReady && playerOptions.sources[0].src">
        <div class="skeleton-shimmer"></div>
        <div class="skeleton-play-btn">
          <div class="skeleton-spinner"></div>
        </div>
        <div class="skeleton-text">视频加载中...</div>
      </div>
      <!-- 无视频时的占位 -->
      <div class="video-placeholder" v-if="!playerOptions.sources[0].src && !pdfuri && !videoReady">
        <div class="placeholder-icon">
          <svg viewBox="0 0 24 24" fill="none">
            <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" stroke-width="2"/>
            <path d="M10 9l5 3-5 3V9z" fill="currentColor"/>
          </svg>
        </div>
        <div class="placeholder-text">课程内容加载中</div>
      </div>
      <!-- 视频播放器 -->
      <video-player
        v-if="playerOptions.sources[0].src"
        class="video-player vjs-custom-skin"
        :class="{ 'video-hidden': !videoReady }"
        ref="videoPlayer"
        :playsinline="true"
        :options="playerOptions"
        :events="['loadstart', 'loadedmetadata', 'seeking', 'seeked', 'stalled']"
        @ready="onPlayerReady($event)"
        @error="onPlayerError($event)"
        @waiting="onPlayerWaiting($event)"
        @stalled="onPlayerWaiting($event)"
        @seeking="onPlayerSeeking($event)"
        @seeked="onPlayerSeeked($event)"
        @play="onPlayerPlay($event)"
        @pause="onPlayerPause($event)"
        @playing="onPlayerPlaying($event)"
        @ended="onPlayerEnded($event)"
        @timeupdate="onPlayerTimeupdate($event)"
        @loadstart="onPlayerLoadstart($event)"
        @loadedmetadata="onPlayerLoadedmetadata($event)"
        @loadeddata="onPlayerLoadeddata($event)"
        @canplay="onPlayerCanplay($event)"
      />
      <!-- 自定义 SVG 播放按钮：与骨架屏圆圈位置/尺寸保持一致 -->
      <div
        class="custom-play-overlay"
        v-if="videoReady && !videoStarted && !playbackNotice && playerOptions.sources[0].src"
        @click="playVideo"
      >
        <div class="custom-play-btn">
          <svg class="custom-play-icon" viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true">
            <path d="M8 5.5v13a1 1 0 0 0 1.5.87l11-6.5a1 1 0 0 0 0-1.74l-11-6.5A1 1 0 0 0 8 5.5z"/>
          </svg>
        </div>
      </div>
      <div v-if="playbackNotice" class="playback-notice" role="status" aria-live="polite">
        <span>{{ playbackNotice }}</span>
        <button type="button" @click="retryPlayback">重新加载视频</button>
      </div>
    </div>

    <!-- PDF预览 -->
    <div class="pdf-container" v-if="pdfuri">
      <iframe :src="pdfuri" class="pdf-iframe" />
    </div>

    <!-- 课程信息卡片 -->
    <div class="course-info-card">
      <div class="course-header">
        <!-- 标题骨架屏 -->
        <div class="title-skeleton" v-if="!Curricula_details.Subject">
          <div class="skeleton-line skeleton-title"></div>
        </div>
        <h2 class="course-title" v-else>{{ Curricula_details.Subject }}</h2>
      </div>
      <!-- 投票按钮骨架屏 -->
      <div class="vote-buttons-skeleton" v-if="!Curricula_details.Subject">
        <div class="skeleton-btn"></div>
        <div class="skeleton-btn"></div>
      </div>
      <div class="vote-buttons" v-else>
        <span
          class="vote-btn"
          :class="{ active: userVote === 1 }"
          @click="handleVote(1)"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="vote-count">{{ likes }}</span>
        </span>
        <span
          class="vote-btn"
          :class="{ active: userVote === -1 }"
          @click="handleVote(-1)"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="vote-count">{{ dislikes }}</span>
        </span>
      </div>
      <p class="course-desc" v-if="Curricula_details.Reamrk">{{ Curricula_details.Reamrk }}</p>

      <!-- 考试入口卡片 - 课程完成后常驻显示 -->
      <div class="exam-entry-card" v-if="showExamCard && Curricula_details.ExamId" @click="goToExam">
        <div class="exam-card-icon">📝</div>
        <div class="exam-card-info">
          <div class="exam-card-title">{{ Curricula_details.ExamName || '课程考试' }}</div>
          <div class="exam-card-desc">课程学习已完成，点击进入考试</div>
        </div>
        <div class="exam-card-arrow">›</div>
      </div>
    </div>

    <!-- 评论区域 - 简洁列表 -->
    <div class="comment-section">
      <!-- 评论列表头部 -->
      <div class="section-header">
        <span class="section-title">全部评论</span>
        <span class="comment-count" v-if="Curricula_details.IsOpenEvaluation">{{ Evaluation_list.length }} 条</span>
      </div>

      <!-- 评论骨架屏 -->
      <div class="comment-skeleton" v-if="!Curricula_details.Subject">
        <div class="skeleton-comment-item" v-for="i in 3" :key="i">
          <div class="skeleton-comment-meta">
            <div class="skeleton-line skeleton-floor"></div>
            <div class="skeleton-line skeleton-author"></div>
          </div>
          <div class="skeleton-line skeleton-content"></div>
          <div class="skeleton-line skeleton-content-short"></div>
        </div>
      </div>

      <!-- 评论列表 -->
      <div class="comment-list" v-else-if="Curricula_details.IsOpenEvaluation">
        <div
          class="comment-item"
          v-for="(item, index) in Evaluation_list"
          :key="item.Id"
        >
          <div class="comment-main">
            <div class="comment-meta">
              <span class="comment-floor">#{{ index + 1 }}</span>
              <span class="comment-author">{{ maskName(item.Name) }}</span>
            </div>
            <div class="comment-body">{{ item.Content }}</div>
            <div class="comment-footer">
              <span class="reply-btn" @click="openCommentPanel(item)">回复</span>
            </div>
          </div>

          <!-- 回复列表 -->
          <div class="reply-list" v-if="item.evaluationVWs && item.evaluationVWs.length > 0">
            <div
              class="reply-item"
              v-for="reply in item.evaluationVWs"
              :key="reply.Id"
            >
              <span class="reply-author">{{ maskName(reply.Name) }}</span>
              <span class="reply-content">{{ reply.Content }}</span>
              <span class="reply-action" @click="openCommentPanel(reply)">回复</span>
            </div>
          </div>
        </div>

        <!-- 空评论状态 -->
        <div v-if="Evaluation_list.length === 0" class="empty-comment">
          <p>暂无评论，点击右下角发表第一条评论</p>
        </div>
      </div>

      <!-- 评论未开启 -->
      <div class="comment-closed" v-else>
        <p>该课程暂未开启评论</p>
      </div>
    </div>

    <!-- 右下角浮窗按钮 -->
    <div 
      class="comment-fab" 
      v-if="Curricula_details.IsOpenEvaluation"
      @click="openCommentPanel(null)"
    >
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span class="fab-badge" v-if="Evaluation_list.length > 0">{{ Evaluation_list.length }}</span>
    </div>

    <!-- 评论弹窗遮罩 -->
    <transition name="fade">
      <div class="comment-overlay" v-if="showCommentPanel" @click="closeCommentPanel"></div>
    </transition>

    <!-- 评论弹窗面板 -->
    <transition name="slide-up">
      <div class="comment-panel" v-if="showCommentPanel">
        <div class="panel-header">
          <span class="panel-title">{{ HF_IF ? `回复 ${replyTarget}` : '发表评论' }}</span>
          <span class="panel-close" @click="closeCommentPanel">×</span>
        </div>
        <div class="panel-body">
          <van-field
            v-model="input_pl"
            ref="bot_field"
            rows="4"
            autosize
            type="textarea"
            class="panel-textarea"
            :placeholder="placeholder_text"
          />
        </div>
        <div class="panel-footer">
          <button class="panel-cancel" @click="closeCommentPanel">取消</button>
          <button class="panel-submit" @click="submit_pl" :disabled="commentSubmitting">发表</button>
        </div>
      </div>
    </transition>

    <!-- 解锁提示弹窗 -->
    <unlock-tip-dialog
      :show.sync="show_unlock_tip"
      :unlock-data="unlockTipData"
      :show-back-button="true"
      @go-course="goToPreCourse"
      @back="goBack"
    />

    <!-- 考试入口弹窗 -->
    <van-dialog
      v-model="showExamEntry"
      title="恭喜完成课程学习!"
      :show-cancel-button="true"
      confirm-button-text="去考试"
      cancel-button-text="稍后"
      confirm-button-color="#d51900"
      @confirm="goToExam"
    >
      <div class="exam-entry-content">
        <p class="exam-entry-text">{{ Curricula_details.ExamName || '点击进入考试' }}</p>
      </div>
    </van-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import {
  WeChatCurriculum_GetCurriculaById,
  WeChatEvaluation_GetEvaluationList,
  WeChatEvaluation_MemberEvaluation,
  CCMWaabeeeehilmrtttuv_SaveMemberCultivate,
  WeChatCurriculumUnlock_BatchCheckUnlockStatus,
  WeChatCurriculumVote_Vote,
  WeChatMemberExam_MemberExamList
} from '../api/api'
import UnlockTipDialog from '../components/UnlockTipDialog.vue'
import { createPlaybackGuard, inspectPlaybackProgress } from '../utils/playbackGuard'
import { reportClientIssue } from '../utils/clientDiagnostics'

// 页面从后台恢复后的可信窗口：窗口内发起的纠正只做一次，
// 避免把系统冻结期间真实播放产生的跳跃误判成用户拖拽并反复拉回。
const RESUME_GRACE_MS = 2000
// programmatic seek 会被播放器同步回抛 seeking 事件，窗口内忽略该回声，
// 避免日志重复与同一事件内的递归校验。
const SEEK_ECHO_GRACE_MS = 150
// MEDIA_ERR_DECODE：解码失败在 iOS/微信上较常见，保留自动重载的自愈能力，
// 超过次数上限后才提示用户手动重载。
const AUTO_RELOAD_ERROR_CODE = 3
const MAX_AUTO_RELOAD = 2

export default {
  name: 'classStudy',
  components: {
    UnlockTipDialog
  },
  data () {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
    const isWechat = /MicroMessenger/i.test(navigator.userAgent)
    return {
      isIOS,
      isWechat,
      show_unlock_tip: false,
      unlockTipData: {},
      showExamEntry: false,
      showExamCard: false,
      courseCompleted: false,
      HF_IF: false,
      showCommentPanel: false,
      replyTarget: '',
      input_pl: '',
      placeholder_text: '说点什么吧...',
      Evaluation_list: [],
      Curricula_details: {},
      Curricula_details_put: {},
      video_time: 60,
      durations: 0,
      durations_Cultivate: 0,
      pdfuri: '',
      playerOptions: {
        autoplay: false,
        muted: false,
        loop: false,
        preload: 'auto',
        language: 'zh-CN',
        aspectRatio: '16:9',
        fluid: true,
        sources: [{ type: 'video/mp4', src: '' }],
        poster: '',
        notSupportedMessage: '此视频暂无法播放，请稍后再试',
        controlBar: {
          timeDivider: true,
          durationDisplay: true,
          remainingTimeDisplay: false,
          fullscreenToggle: true,
          progressControl: true
        }
      },
      form_pl: {
        Content: '',
        ParentId: '',
        ToEvaluationId: '',
        MemberID: '',
        CourseId: ''
      },
      // 投票相关
      likes: 0,
      dislikes: 0,
      userVote: 0,
      voteLoading: false,
      // 评论提交锁
      commentSubmitting: false,
      // 视频加载状态
      videoReady: false,
      // 是否已开始播放（用于隐藏自定义播放按钮）
      videoStarted: false,
      // 骨架屏兜底定时器（iOS18 等老设备不会自动触发 canplay）
      videoReadyTimer: null,
      pageLoadToken: 0,
      pdfProgressTimer: null,
      playbackGuard: createPlaybackGuard(),
      initialPositionRestored: false,
      resumePosition: null,
      playbackNotice: '',
      playbackTimer: null,
      playbackEvents: [],
      playbackRetryCount: 0,
      lastObservedTime: 0,
      lastAdvancingAt: 0,
      playbackStallReported: false,
      resumedAt: 0,
      seekEchoUntil: 0,
      lastTimeupdateAt: 0,
      completionSaved: false,
      completionSaving: false,
      lastCompletionAttemptAt: 0,
      cachedTotalDuration: 0,
      studyAttempt: 0,
      // 全屏/PiP 播放进度兜底相关（覆盖 iOS/Android 系统全屏与画中画，不影响原有播放/上报流程）
      fullscreenHandlerAttached: false,
      fullscreenVideoEl: null,
      iosFullscreenHandler: null,
      x5FullscreenHandler: null,
      standardFullscreenHandler: null,
      pipHandler: null,
      enterPipHandler: null,
      leavePipHandler: null,
      fullscreenProgressTimer: null,
      visibilityBeaconHandler: null,
      pageHideBeaconHandler: null
    }
  },
  async mounted () {
    // 页面隐藏/关闭时的兜底进度上报，与原有 timeupdate/ended 上报流程互不影响、互为补充
    this.attachLifecycleBeaconHandlers()
    await this.initializePage()
  },
  watch: {
    '$route.fullPath' (newPath, oldPath) {
      if (newPath !== oldPath) {
        this.initializePage()
      }
    }
  },
  beforeDestroy () {
    this.pageLoadToken += 1
    this.stopPlaybackMonitor()
    this.detachFullscreenHandler()
    this.detachLifecycleBeaconHandlers()
    if (this.pdfProgressTimer) {
      clearTimeout(this.pdfProgressTimer)
      this.pdfProgressTimer = null
    }
    if (this.videoReadyTimer) {
      clearTimeout(this.videoReadyTimer)
      this.videoReadyTimer = null
    }
    if (this.showCommentPanel) {
      this.closeCommentPanel()
    }
  },
  methods: {
    resetPageState () {
      this.stopPlaybackMonitor()
      this.playbackGuard = createPlaybackGuard()
      this.initialPositionRestored = false
      this.resumePosition = null
      this.playbackNotice = ''
      this.playbackEvents = []
      this.playbackRetryCount = 0
      this.lastObservedTime = 0
      this.lastAdvancingAt = 0
      this.playbackStallReported = false
      this.resumedAt = 0
      this.seekEchoUntil = 0
      // 切换课程会重建播放器实例，旧的全屏监听需先解绑，避免残留引用绑到已销毁的video元素上
      this.detachFullscreenHandler()
      if (this.pdfProgressTimer) {
        clearTimeout(this.pdfProgressTimer)
        this.pdfProgressTimer = null
      }
      if (this.showCommentPanel) {
        this.closeCommentPanel()
      }
      this.show_unlock_tip = false
      this.unlockTipData = {}
      this.showExamEntry = false
      this.showExamCard = false
      this.courseCompleted = false
      this.Evaluation_list = []
      this.Curricula_details = {}
      this.Curricula_details_put = {}
      this.video_time = 60
      this.durations = 0
      this.durations_Cultivate = 0
      this.pdfuri = ''
      this.playerOptions.sources[0].src = ''
      this.playerOptions.poster = ''
      this.likes = 0
      this.dislikes = 0
      this.userVote = 0
      this.videoReady = false
      this.videoStarted = false
      this.lastTimeupdateAt = 0
      this.completionSaved = false
      this.completionSaving = false
      this.lastCompletionAttemptAt = 0
      this.cachedTotalDuration = 0
      this.studyAttempt = 0
    },
    async initializePage () {
      const pageLoadToken = ++this.pageLoadToken
      const courseId = this.$route.query.ids
      const retrain = this.$route.query.retrain

      this.resetPageState()

      // 检查课程是否已解锁
      const isUnlocked = await this.checkUnlockStatus(courseId, pageLoadToken)
      if (!isUnlocked || pageLoadToken !== this.pageLoadToken) {
        return
      }

      // 加载课程详情
      const { data } = await WeChatCurriculum_GetCurriculaById({
        Id: courseId
      })

      if (pageLoadToken !== this.pageLoadToken) {
        return
      }

      const response = data.response || {}

      this.Curricula_details = response
      this.Curricula_details_put = { ...response }
      this.studyAttempt = typeof response.StudyAttempt === 'number' ? response.StudyAttempt : 0

      // 初始化投票数据
      this.likes = response.Likes || 0
      this.dislikes = response.Dislikes || 0
      this.userVote = response.UserVote || 0

      this.video_time = Math.max(0, Number(response.ViewedSeconds) || 0)
      this.durations_Cultivate = this.video_time
      this.playbackGuard = createPlaybackGuard(this.video_time, Date.now())

      if (response.Link && typeof response.Link === 'string') {
        if (/\.mp4(?:[?#]|$)/i.test(response.Link)) {
          this.playerOptions.sources[0].src = response.Link
          this.playerOptions.poster = response.Thumbnail
          // 启动骨架屏兜底：iOS18 老设备不预加载，canplay 可能一直不触发，
          // 超时后强制隐藏骨架屏，露出原生播放按钮让用户可点击播放
          this.startVideoReadyFallback()
        } else {
          this.pdfuri = 'https://preview.51i.cc/onlinePrevieww?url=' + window.btoa(response.Link) + '&watermarkTxt=Acuvue iLearn'
          this.savePDFProgress(response.CurriculumId)
        }
      }

      // 重新培训模式：允许自由拖拽进度条
      if (retrain === '1' || retrain === 1) {
        this.courseCompleted = true
      }

      await this.loadComments(courseId, pageLoadToken)
    },
    // 处理投票
    async handleVote (voteType) {
      // 防止重复点击
      if (this.voteLoading) return

      // 检查登录状态
      const openId = localStorage.getItem('openid')
      if (!openId) {
        this.$toast('请先登录')
        return
      }

      this.voteLoading = true
      try {
        const { data } = await WeChatCurriculumVote_Vote({
          CurriculumId: parseInt(this.$route.query.ids),
          VoteType: voteType
        })

        if (data.success) {
          // 更新本地状态
          this.likes = data.response.Likes
          this.dislikes = data.response.Dislikes
          this.userVote = data.response.UserVote
        } else {
          this.$toast(data.msg || '操作失败')
        }
      } catch (e) {
        this.$toast('网络错误，请重试')
      } finally {
        this.voteLoading = false
      }
    },
    maskName (name) {
      if (!name) return '匿名'
      return name.charAt(0) + '*'.repeat(Math.max(name.length - 1, 1))
    },
    clear_hf () {
      this.HF_IF = false
      this.input_pl = ''
      this.placeholder_text = '说点什么吧...'
      this.replyTarget = ''
    },
    openCommentPanel (item) {
      if (item) {
        // 回复模式
        this.form_pl = item
        this.placeholder_text = `回复 ${this.maskName(item.Name)}...`
        this.replyTarget = this.maskName(item.Name)
        this.HF_IF = true
      } else {
        // 新评论模式
        this.clear_hf()
      }
      this.showCommentPanel = true
      // 锁定背景滚动
      this._scrollTop = document.documentElement.scrollTop || document.body.scrollTop
      document.body.style.position = 'fixed'
      document.body.style.top = `-${this._scrollTop}px`
      document.body.style.width = '100%'
      this.$nextTick(() => {
        this.$refs.bot_field && this.$refs.bot_field.focus()
      })
    },
    closeCommentPanel () {
      this.showCommentPanel = false
      this.clear_hf()
      // 恢复背景滚动
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      window.scrollTo(0, this._scrollTop || 0)
    },
    async loadComments (courseId = this.$route.query.ids, pageLoadToken = this.pageLoadToken) {
      const { data } = await WeChatEvaluation_GetEvaluationList({
        CourseId: courseId
      })
      if (pageLoadToken !== this.pageLoadToken) {
        return
      }
      this.Evaluation_list = data.response || []
    },
    async submit_pl () {
      if (this.commentSubmitting) return

      if (this.input_pl.match(/^[ ]*$/)) {
        this.$toast('请输入评论')
        return
      }

      this.commentSubmitting = true
      try {
        if (this.HF_IF) {
          this.form_pl.Content = this.input_pl
        } else {
          this.form_pl = { ...this.Curricula_details }
          this.form_pl.Content = this.input_pl
        }

        const { data } = await WeChatEvaluation_MemberEvaluation(this.form_pl)
        if (data.status === 200) {
          this.closeCommentPanel()
          this.loadComments()
          this.$toast('评论提交成功，审核通过后将展示您的评论')
        } else {
          this.$toast(data.msg)
        }
      } finally {
        this.commentSubmitting = false
      }
    },
    getPlayerCurrentTime (player) {
      if (!player) return 0
      const currentTime = typeof player.currentTime === 'function'
        ? player.currentTime()
        : player.cache_ && player.cache_.currentTime
      return typeof currentTime === 'number' && isFinite(currentTime) ? currentTime : 0
    },
    getPlayerDuration (player) {
      if (!player) return this.cachedTotalDuration || 0
      const liveDuration = typeof player.duration === 'function'
        ? player.duration()
        : player.cache_ && player.cache_.duration
      if (typeof liveDuration === 'number' && isFinite(liveDuration) && liveDuration > 0) {
        this.cachedTotalDuration = liveDuration
      }
      const duration = (typeof liveDuration === 'number' && isFinite(liveDuration) && liveDuration > 0)
        ? liveDuration
        : this.cachedTotalDuration
      return (typeof duration === 'number' && isFinite(duration) && duration > 0) ? duration : 0
    },
    syncPlayerProgress (player) {
      const currentTime = this.getPlayerCurrentTime(player)
      const totalDuration = this.getPlayerDuration(player)
      if (totalDuration > 0) {
        this.Curricula_details_put.TotalSeconds = totalDuration
      }
      this.Curricula_details_put.ViewedSeconds = currentTime
      return { currentTime, totalDuration }
    },
    markCourseCompleted (player) {
      if (this.completionSaved || this.completionSaving) return
      const now = Date.now()
      if (this.lastCompletionAttemptAt && now - this.lastCompletionAttemptAt < 3000) return
      this.lastCompletionAttemptAt = now

      const progress = this.syncPlayerProgress(player)
      const completedSeconds = progress.totalDuration > 0
        ? progress.totalDuration
        : Math.max(progress.currentTime, this.durations_Cultivate, this.video_time)

      this.Curricula_details_put.TotalSeconds = completedSeconds
      this.durations = completedSeconds
      this.durations_Cultivate = completedSeconds
      this.courseCompleted = true
      if (this.Curricula_details.ExamId) {
        this.showExamCard = true
      }
      this.completionSaving = true
      const pageLoadToken = this.pageLoadToken
      this.saveProgress(true).then(ok => {
        if (pageLoadToken !== this.pageLoadToken || this._isDestroyed) return
        this.completionSaving = false
        if (ok) {
          this.completionSaved = true
          if (this.Curricula_details.ExamId) {
            this.showExamEntry = true
          }
        } else {
          this.completionSaving = false
        }
      })
    },
    validatePlayerProgress (player, seeking) {
      if (!this.initialPositionRestored || !player) return false
      const currentTime = this.getPlayerCurrentTime(player)
      const now = Date.now()
      if (this.playbackGuard.correctionTarget !== null && (!this.videoStarted || document.visibilityState === 'hidden')) {
        this.playbackGuard.correctionStartedAt = now
      }
      // 页面不可见或刚从后台恢复时，进度跳跃可能来自系统冻结期间的真实播放，
      // 这类纠正只做一次（soft），不重复拉回，避免误伤 iOS/微信全屏、PiP 与后台播放场景。
      const soft = document.visibilityState !== 'visible' ||
        (this.resumedAt > 0 && now - this.resumedAt < RESUME_GRACE_MS)
      const result = inspectPlaybackProgress(this.playbackGuard, currentTime, now, {
        unrestricted: this.courseCompleted,
        soft: soft,
        seeking: seeking === undefined ? this.readPlayerValue(player, 'seeking', false) : seeking,
        playbackRate: this.readPlayerValue(player, 'playbackRate', 1),
        paused: this.readPlayerValue(player, 'paused', true)
      })
      if (result.correctionTarget !== undefined) {
        this.recordPlaybackEvent(result.retry ? 'seek-correction-retry' : 'seek-correction', player)
        try {
          player.currentTime(result.correctionTarget)
          this.seekEchoUntil = Date.now() + SEEK_ECHO_GRACE_MS
        } catch (e) {
          this.playbackGuard.blocked = true
          this.showPlaybackFailure('视频定位失败，请重新加载后继续', player, 'seek-error')
        }
      }
      if (result.blocked) {
        this.showPlaybackFailure('视频进度恢复超时，请重新加载后继续', player, 'seek-timeout')
      }
      return !!result.accepted
    },
    onPlayerTimeupdate (player) {
      if (!this.validatePlayerProgress(player)) return
      const { currentTime, totalDuration } = this.syncPlayerProgress(player)
      this.durations = currentTime
      this.lastTimeupdateAt = Date.now()
      if (Math.abs(currentTime - this.lastObservedTime) > 0.1) {
        this.lastObservedTime = currentTime
        this.lastAdvancingAt = Date.now()
        this.playbackStallReported = false
        this.playbackNotice = ''
      }
      if (currentTime - this.durations_Cultivate > 5) {
        this.durations_Cultivate = Math.max(this.durations_Cultivate, this.playbackGuard.maxWatched)
        this.saveProgress()
      }
      if (!this.completionSaved && !this.completionSaving && totalDuration > 0 && currentTime + 10 >= totalDuration) {
        this.markCourseCompleted(player)
      }
    },
    saveProgress (isComplete) {
      const pageLoadToken = this.pageLoadToken
      const viewedSeconds = isComplete
        ? this.Curricula_details_put.TotalSeconds
        : this.durations_Cultivate

      return Promise.resolve(
        CCMWaabeeeehilmrtttuv_SaveMemberCultivate({
          CurriculumId: this.Curricula_details_put.CurriculumId,
          TotalSeconds: this.Curricula_details_put.TotalSeconds,
          ViewedSeconds: viewedSeconds,
          StudyAttempt: this.studyAttempt
        })
      ).then(res => {
        const ok = !!(res && res.data && res.data.success)
        if (!ok && pageLoadToken === this.pageLoadToken && !this._isDestroyed) {
          const player = this.$refs.videoPlayer && this.$refs.videoPlayer.player
          this.reportPlaybackIssue('progress-save-rejected', player, 'warning')
        }
        return ok
      }).catch(() => false)
    },
    savePDFProgress (curriculumId) {
      if (this.pdfProgressTimer) {
        clearTimeout(this.pdfProgressTimer)
      }
      this.pdfProgressTimer = setTimeout(() => {
        CCMWaabeeeehilmrtttuv_SaveMemberCultivate({
          CurriculumId: curriculumId,
          TotalSeconds: 99999,
          ViewedSeconds: 99999,
          StudyAttempt: this.studyAttempt
        })
        this.pdfProgressTimer = null
      }, 5000)
    },
    readPlayerValue (player, method, fallback) {
      try {
        return player && typeof player[method] === 'function' ? player[method]() : fallback
      } catch (e) {
        return fallback
      }
    },
    onPlayerReady (player) {
      this.stopPlaybackMonitor()
      this.detachFullscreenHandler()
      this.applyWechatVideoAttrs()
      this.attachFullscreenHandler(player)
      this.startPlaybackMonitor(player)
      if (this.readPlayerValue(player, 'readyState', 0) >= 1) this.restorePlayerPosition(player)
    },
    onPlayerLoadstart (player) {
      this.recordPlaybackEvent('loadstart', player)
    },
    onPlayerLoadedmetadata (player) {
      this.markVideoReady()
      this.restorePlayerPosition(player)
    },
    onPlayerLoadeddata (player) {
      this.markVideoReady()
      this.recordPlaybackEvent('loadeddata', player)
      this.restorePlayerPosition(player)
    },
    restorePlayerPosition (player) {
      if (this.initialPositionRestored) return
      const totalDuration = this.getPlayerDuration(player)
      if (totalDuration <= 0) return
      this.Curricula_details_put.TotalSeconds = totalDuration
      const wasCompleted = this.video_time > 0 && this.video_time >= totalDuration - 2
      const position = Math.min(totalDuration, this.resumePosition !== null
        ? this.resumePosition : (wasCompleted ? 0 : this.video_time))
      this.initialPositionRestored = true
      this.resumePosition = null
      this.playbackGuard = createPlaybackGuard(position, Date.now())
      this.playbackGuard.maxWatched = Math.max(position, Math.min(totalDuration, this.durations_Cultivate))
      this.durations = position
      if (wasCompleted) {
        this.courseCompleted = true
        this.markCourseCompleted(player)
        this.durations = position
      }
      if (Math.abs(this.getPlayerCurrentTime(player) - position) > 0.5) {
        this.playbackGuard.correctionTarget = position
        this.playbackGuard.correctionStartedAt = Date.now()
        try {
          player.currentTime(position)
        } catch (e) {
          this.playbackGuard.blocked = true
          this.showPlaybackFailure('无法恢复观看进度，请重新加载', player, 'restore-error')
        }
      }
    },
    reloadPlayer () {
      const src = this.playerOptions.sources[0].src
      if (!src) return
      const pageLoadToken = this.pageLoadToken
      this.resumePosition = this.playbackGuard.position
      this.initialPositionRestored = false
      this.stopPlaybackMonitor()
      this.detachFullscreenHandler()
      this.videoStarted = false
      this.videoReady = false
      this.playbackNotice = ''
      this.playbackStallReported = false
      this.playbackRetryCount += 1
      this.playerOptions.sources[0].src = ''
      this.$nextTick(() => {
        if (pageLoadToken !== this.pageLoadToken || this._isDestroyed) return
        this.playerOptions.sources[0].src = src
        this.startVideoReadyFallback()
      })
    },
    retryPlayback () {
      const player = this.$refs.videoPlayer && this.$refs.videoPlayer.player
      this.reportPlaybackIssue('manual-retry', player, 'warning')
      this.reloadPlayer()
    },
    onPlayerPlay (player) {
      if (!this.playbackGuard.updatedAt) this.playbackGuard.updatedAt = Date.now()
      if (!this.videoStarted && this.playbackGuard.correctionTarget !== null) {
        this.playbackGuard.correctionStartedAt = Date.now()
      }
      this.videoStarted = true
      this.lastAdvancingAt = Date.now()
      this.recordPlaybackEvent('play', player)
      this.attachFullscreenHandler(player)
      this.startPlaybackMonitor(player)
    },
    onPlayerPause (player) {
      this.recordPlaybackEvent('pause', player)
      if (this.initialPositionRestored && this.playbackGuard.correctionTarget === null) {
        this.onPlayerTimeupdate(player)
        this.playbackGuard.updatedAt = 0
      }
    },
    onPlayerSeeking (player) {
      // 忽略 programmatic seek 同步回抛的 seeking 回声
      if (Date.now() < this.seekEchoUntil) return
      this.recordPlaybackEvent('seeking', player)
      this.validatePlayerProgress(player, true)
    },
    onPlayerSeeked (player) {
      this.recordPlaybackEvent('seeked', player)
      this.onPlayerTimeupdate(player)
      this.lastAdvancingAt = Date.now()
    },
    onPlayerError (player) {
      const error = this.readPlayerValue(player, 'error', null)
      if (!error) return
      this.recordPlaybackEvent('error', player)
      // 解码失败先自动重载自愈（沿用历史行为），额度用尽后再提示用户手动重载
      if (error.code === AUTO_RELOAD_ERROR_CODE && this.playbackRetryCount < MAX_AUTO_RELOAD) {
        this.reportPlaybackIssue('media-error-auto-reload', player, 'warning')
        this.reloadPlayer()
        return
      }
      const message = error.code === 4 ? '当前设备无法播放此视频，请联系管理员' : '视频加载或解码失败，请重新加载后继续'
      this.showPlaybackFailure(message, player, 'media-error')
    },
    onPlayerWaiting (player) {
      this.recordPlaybackEvent('waiting', player)
      this.startPlaybackMonitor(player)
    },
    // 自定义播放按钮：用户手势触发 player.play()
    playVideo () {
      const playerRef = this.$refs.videoPlayer
      if (!playerRef || !playerRef.player) return
      try {
        const result = playerRef.player.play()
        if (result && typeof result.catch === 'function') {
          result.catch(() => {})
        }
      } catch (e) {
        // ignore
      }
    },
    onPlayerPlaying (player) {
      this.recordPlaybackEvent('playing', player)
      this.lastAdvancingAt = Date.now()
      if (!this.playbackGuard.blocked) this.playbackNotice = ''
    },
    onPlayerCanplay (player) {
      this.markVideoReady()
      this.restorePlayerPosition(player)
    },
    recordPlaybackEvent (event, player) {
      this.playbackEvents.push({ event, currentTime: this.getPlayerCurrentTime(player), at: Date.now() })
      if (this.playbackEvents.length > 20) this.playbackEvents.shift()
    },
    reportPlaybackIssue (event, player, level = 'error') {
      const error = this.readPlayerValue(player, 'error', null)
      this.recordPlaybackEvent(event, player)
      reportClientIssue({
        kind: 'video',
        level,
        message: event,
        courseId: Number(this.Curricula_details_put.CurriculumId) || 0,
        context: {
          event,
          currentTime: this.getPlayerCurrentTime(player),
          totalDuration: this.getPlayerDuration(player),
          lastAcceptedTime: this.playbackGuard.position,
          maxWatchedTime: this.playbackGuard.maxWatched,
          readyState: this.readPlayerValue(player, 'readyState', 0),
          networkState: this.readPlayerValue(player, 'networkState', 0),
          paused: this.readPlayerValue(player, 'paused', true),
          seeking: this.readPlayerValue(player, 'seeking', false),
          bufferedEnd: this.readPlayerValue(player, 'bufferedEnd', 0),
          playbackRate: this.readPlayerValue(player, 'playbackRate', 1),
          visibility: document.visibilityState,
          online: navigator.onLine !== false,
          isIOS: this.isIOS,
          isWechat: this.isWechat,
          errorCode: error ? error.code : 0,
          retryCount: this.playbackRetryCount,
          studyAttempt: this.studyAttempt,
          events: this.playbackEvents.slice()
        }
      })
    },
    showPlaybackFailure (message, player, event) {
      this.markVideoReady()
      this.playbackNotice = message
      if (!this.playbackStallReported) {
        this.playbackStallReported = true
        this.reportPlaybackIssue(event, player)
      }
      if (this.playbackGuard.blocked && !this.readPlayerValue(player, 'paused', true) && typeof player.pause === 'function') player.pause()
    },
    startPlaybackMonitor (player) {
      if (this.playbackTimer) return
      this.lastAdvancingAt = Date.now()
      this.playbackTimer = setInterval(() => {
        if (!player || this.readPlayerValue(player, 'isDisposed', false)) return
        if (document.visibilityState === 'hidden') {
          this.lastAdvancingAt = Date.now()
          return
        }
        if (this.playbackGuard.correctionTarget !== null) this.validatePlayerProgress(player)
        if (!this.videoStarted || this.readPlayerValue(player, 'paused', true)) return
        const currentTime = this.getPlayerCurrentTime(player)
        if (Math.abs(currentTime - this.lastObservedTime) > 0.1) {
          this.onPlayerTimeupdate(player)
        } else if (Date.now() - this.lastAdvancingAt >= 15000) {
          this.showPlaybackFailure('视频暂未继续播放，请检查网络或重新加载', player, 'playback-stalled')
        }
      }, 2000)
    },
    stopPlaybackMonitor () {
      if (this.playbackTimer) clearInterval(this.playbackTimer)
      this.playbackTimer = null
    },
    // 统一的「视频就绪」处理：隐藏骨架屏并清理兜底定时器
    markVideoReady () {
      if (this.videoReadyTimer) {
        clearTimeout(this.videoReadyTimer)
        this.videoReadyTimer = null
      }
      if (this.videoReady) return
      this.videoReady = true
      // 为微信/iOS环境添加video元素属性，优化播放体验
      this.applyWechatVideoAttrs()
    },
    // 兜底定时器：iOS18 老设备不预加载，loadedmetadata/canplay 可能迟迟不触发，
    // 超时后强制隐藏骨架屏，露出原生播放按钮供用户点击播放
    startVideoReadyFallback () {
      if (this.videoReadyTimer) {
        clearTimeout(this.videoReadyTimer)
      }
      this.videoReadyTimer = setTimeout(() => {
        this.markVideoReady()
      }, 3000)
    },
    applyWechatVideoAttrs () {
      try {
        if (!this.$refs.videoPlayer) return
        const videoEl = this.$refs.videoPlayer.player.tech({ IWillNotUseThisInPlugins: true }).el()
        if (!videoEl) return
        // 微信内置浏览器专用属性
        videoEl.setAttribute('webkit-playsinline', 'true')
        videoEl.setAttribute('x5-video-player-type', 'h5')
        videoEl.setAttribute('x5-video-player-fullscreen', 'true')
        videoEl.setAttribute('x5-video-orientation', 'portraint')
        // iOS WKWebView 属性
        videoEl.setAttribute('playsinline', 'true')
        videoEl.setAttribute('x-webkit-airplay', 'allow')
      } catch (e) {
        console.warn('设置微信视频属性失败:', e)
      }
    },
    onPlayerEnded (player) {
      if (this.validatePlayerProgress(player, false)) this.markCourseCompleted(player)
    },
    // 附加全屏/PiP 进度兜底监听（覆盖 iOS 系统全屏、Android X5 全屏、标准全屏、iOS/Android 画中画）
    // 仅作为 onPlayerTimeupdate / onPlayerEnded 之外的补充触发点，不改变原有完成判定逻辑
    attachFullscreenHandler (player) {
      if (this.fullscreenHandlerAttached) return
      try {
        const videoEl = player && typeof player.tech === 'function'
          ? player.tech({ IWillNotUseThisInPlugins: true }).el()
          : null
        if (!videoEl) return

        // 退出全屏/PiP 时的统一处理：同步进度并检查完成状态
        const handleExit = () => {
          this.stopFullscreenProgressTimer()
          this.recordPlaybackEvent('fullscreen-exit', player)
          this.lastAdvancingAt = Date.now()
          this.onPlayerTimeupdate(player)
        }

        // 进入全屏/PiP 时的快照保存：确保进入前的进度不丢失，并启动定时器兜底
        const handleEnter = () => {
          this.recordPlaybackEvent('fullscreen-enter', player)
          this.onPlayerTimeupdate(player)
          this.startFullscreenProgressTimer(player)
        }

        // iOS 系统全屏退出
        this.iosFullscreenHandler = handleExit
        videoEl.addEventListener('webkitendfullscreen', handleExit)

        // Android 微信 X5 全屏退出
        this.x5FullscreenHandler = handleExit
        videoEl.addEventListener('x5videoexitfullscreen', handleExit)

        // 标准全屏变化（Android Chrome 等非微信浏览器）
        this.standardFullscreenHandler = () => {
          if (document.fullscreenElement) {
            handleEnter()
          } else {
            handleExit()
          }
        }
        document.addEventListener('fullscreenchange', this.standardFullscreenHandler)

        // iOS PiP 及全屏（presentation mode 变化）
        this.pipHandler = () => {
          const mode = videoEl.webkitPresentationMode
          if (mode === 'picture-in-picture' || mode === 'fullscreen') {
            handleEnter()
          } else if (mode === 'inline') {
            handleExit()
          }
        }
        videoEl.addEventListener('webkitpresentationmodechanged', this.pipHandler)

        // Android PiP
        this.enterPipHandler = handleEnter
        this.leavePipHandler = handleExit
        videoEl.addEventListener('enterpictureinpicture', handleEnter)
        videoEl.addEventListener('leavepictureinpicture', handleExit)

        this.fullscreenVideoEl = videoEl
        this.fullscreenHandlerAttached = true
      } catch (e) {
        // ignore，不影响原有播放/上报流程
      }
    },
    detachFullscreenHandler () {
      this.stopFullscreenProgressTimer()
      if (this.fullscreenVideoEl) {
        if (this.iosFullscreenHandler) {
          this.fullscreenVideoEl.removeEventListener('webkitendfullscreen', this.iosFullscreenHandler)
        }
        if (this.x5FullscreenHandler) {
          this.fullscreenVideoEl.removeEventListener('x5videoexitfullscreen', this.x5FullscreenHandler)
        }
        if (this.pipHandler) {
          this.fullscreenVideoEl.removeEventListener('webkitpresentationmodechanged', this.pipHandler)
        }
        if (this.enterPipHandler) {
          this.fullscreenVideoEl.removeEventListener('enterpictureinpicture', this.enterPipHandler)
        }
        if (this.leavePipHandler) {
          this.fullscreenVideoEl.removeEventListener('leavepictureinpicture', this.leavePipHandler)
        }
      }
      if (this.standardFullscreenHandler) {
        document.removeEventListener('fullscreenchange', this.standardFullscreenHandler)
      }
      this.fullscreenVideoEl = null
      this.iosFullscreenHandler = null
      this.x5FullscreenHandler = null
      this.standardFullscreenHandler = null
      this.pipHandler = null
      this.enterPipHandler = null
      this.leavePipHandler = null
      this.fullscreenHandlerAttached = false
    },
    // 全屏/PiP 期间的进度兜底定时器：timeupdate 可能停发，定时器确保进度持续保存
    startFullscreenProgressTimer (player) {
      this.stopFullscreenProgressTimer()
      this.fullscreenProgressTimer = setInterval(() => {
        if (player && !this.readPlayerValue(player, 'isDisposed', false)) this.onPlayerTimeupdate(player)
      }, 5000)
    },
    stopFullscreenProgressTimer () {
      if (this.fullscreenProgressTimer) {
        clearInterval(this.fullscreenProgressTimer)
        this.fullscreenProgressTimer = null
      }
    },
    // 页面隐藏（切后台/锁屏/关闭标签）或即将卸载时，用 sendBeacon 补发一次当前进度。
    // sendBeacon 请求不受页面冻结/卸载打断，用于弥补 timeupdate/ended 因页面被系统提前冻结而未发出的最后一次上报。
    // 仅作为兜底补充，不改变原有 saveProgress/markCourseCompleted 判定逻辑，也不重复触发已保存的完成状态。
    sendProgressBeacon () {
      try {
        if (!this.Curricula_details_put || !this.Curricula_details_put.CurriculumId) return
        if (!navigator.sendBeacon) return
        const player = this.$refs.videoPlayer && this.$refs.videoPlayer.player
        const totalDuration = this.getPlayerDuration(player)
        if (this.initialPositionRestored) this.validatePlayerProgress(player)
        const currentTime = this.playbackGuard.position
        const isNearComplete = !this.completionSaved && (
          (totalDuration > 0 && currentTime + 10 >= totalDuration) || this.courseCompleted
        )
        const viewedSeconds = isNearComplete
          ? (totalDuration > 0 ? totalDuration : Math.max(currentTime, this.durations_Cultivate, this.video_time, 1))
          : Math.max(currentTime, this.durations_Cultivate)
        if (viewedSeconds <= 0) return
        const openId = localStorage.getItem('openid')
        const payload = {
          Openid: openId,
          CurriculumId: this.Curricula_details_put.CurriculumId,
          TotalSeconds: totalDuration > 0 ? totalDuration : this.Curricula_details_put.TotalSeconds,
          ViewedSeconds: viewedSeconds,
          StudyAttempt: this.studyAttempt
        }
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
        const queued = navigator.sendBeacon(`${axios.defaults.baseURL}/api/WeChatMemberCultivate/SaveMemberCultivate`, blob)
        if (!queued) {
          this.reportPlaybackIssue('progress-beacon-rejected', player, 'warning')
        } else if (isNearComplete) {
          // beacon 已将完成数据可靠送达后端，此处只需同步本地标志位：
          // - completionSaved：阻止 markCourseCompleted 重复执行
          // - courseCompleted：解锁进度条拖拽限制
          // - showExamCard：常驻卡片提示（非弹窗，不突兀）
          // 不调 saveProgress(true)（beacon 已送达，避免不可靠的 axios 请求）
          // 不设 showExamEntry（避免用户临时切 App 回来后突然弹考试弹窗）
          this.completionSaved = true
          this.courseCompleted = true
          if (this.Curricula_details.ExamId) {
            this.showExamCard = true
          }
        }
      } catch (e) {
        // ignore，不影响原有播放/上报流程
      }
    },
    attachLifecycleBeaconHandlers () {
      if (this.visibilityBeaconHandler || this.pageHideBeaconHandler) return
      this.visibilityBeaconHandler = () => {
        if (document.visibilityState === 'hidden') {
          this.sendProgressBeacon()
        } else {
          const player = this.$refs.videoPlayer && this.$refs.videoPlayer.player
          this.lastAdvancingAt = Date.now()
          // 标记「刚从后台恢复」，供纠正逻辑判定为不可信上下文（只纠正一次）
          this.resumedAt = Date.now()
          if (this.playbackGuard.correctionTarget !== null) this.playbackGuard.correctionStartedAt = Date.now()
          if (player) this.onPlayerTimeupdate(player)
        }
      }
      this.pageHideBeaconHandler = () => {
        this.sendProgressBeacon()
      }
      document.addEventListener('visibilitychange', this.visibilityBeaconHandler)
      window.addEventListener('pagehide', this.pageHideBeaconHandler)
    },
    detachLifecycleBeaconHandlers () {
      if (this.visibilityBeaconHandler) {
        document.removeEventListener('visibilitychange', this.visibilityBeaconHandler)
        this.visibilityBeaconHandler = null
      }
      if (this.pageHideBeaconHandler) {
        window.removeEventListener('pagehide', this.pageHideBeaconHandler)
        this.pageHideBeaconHandler = null
      }
    },
    // 跳转到考试页面
    async goToExam () {
      if (!this.Curricula_details.ExamId) return
      try {
        // 先获取用户考试记录，拿到完整的 MemberExamId 等参数
        const { data } = await WeChatMemberExam_MemberExamList({})
        const examList = (data.response && data.response.data) || []
        const examItem = examList.find(e => e.ExamId === this.Curricula_details.ExamId)
        if (examItem) {
          this.$router.push({
            path: '/examination',
            query: examItem
          })
        } else {
          this.$toast('暂无考试记录，请稍后再试')
        }
      } catch (e) {
        this.$toast('获取考试信息失败')
      }
    },
    // 检查课程解锁状态
    async checkUnlockStatus (courseId = this.$route.query.ids, pageLoadToken = this.pageLoadToken) {
      const openId = localStorage.getItem('openid')
      
      if (!openId) {
        return true // 未登录时默认允许访问
      }

      try {
        const { data } = await WeChatCurriculumUnlock_BatchCheckUnlockStatus([parseInt(courseId)], openId)
        if (pageLoadToken !== this.pageLoadToken) {
          return false
        }
        if (data && data.success && data.response && data.response.length > 0) {
          const status = data.response[0]
          if (!status.IsUnlocked) {
            // 课程未解锁，显示解锁弹窗
            this.unlockTipData = {
              UnlockRuleDesc: status.UnlockRuleDesc || '需完成前置课程',
              UnlockProgress: status.UnlockProgress || '',
              UnfinishedPreCurriculums: status.UnfinishedPreCurriculums || [],
              PreCurriculumList: status.PreCurriculumList || [],
              UnlockRules: status.UnlockRules || []
            }
            this.show_unlock_tip = true
            return false
          }
        }
        return true
      } catch (e) {
        console.error('检查解锁状态失败:', e)
        return true // 检查失败时默认允许访问
      }
    },
    // 跳转到前置课程
    goToPreCourse (course) {
      this.show_unlock_tip = false
      this.$nextTick(() => {
        this.$router.replace({
          path: '/classStudy',
          query: { ids: course.Id }
        })
      })
    },
    // 返回首页
    goBack () {
      this.show_unlock_tip = false
      this.$nextTick(() => {
        this.$router.replace({ path: '/home' })
      })
    }
  }
}
</script>

<style scoped>
.study-page {
  min-height: 100vh;
  background: #f5f9fc;
  padding-bottom: 20px;
  /* 移除 transform 以修复 iOS 上 fixed 弹窗定位问题 */
  /* -webkit-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden; */
  -webkit-overflow-scrolling: touch;
}

/* 顶部标题栏 */
.page-header {
  background: #fff;
  padding: 16px 20px;
  text-align: center;
  border-bottom: 1px solid #f0f4f8;
}

.page-title {
  font-size: 18px;
  font-weight: 800;
  color: #1a2a3a;
  margin: 0;
  letter-spacing: 1px;
}

/* 视频容器 */
.video-container {
  background: #000000;
  position: relative;
  width: 100%;
  /* 默认16:9比例，避免页面跳动 */
  padding-top: 56.25%;
  overflow: hidden;
}

.video-container.video-loading {
  background: #1a1a1a;
}

.playback-notice {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(12, 20, 30, 0.88);
  color: #fff;
  text-align: center;
  font-size: 14px;
  line-height: 1.6;
}

.playback-notice button {
  margin-top: 14px;
  padding: 10px 22px;
  min-height: 44px;
  border: 1px solid #fff;
  border-radius: 6px;
  color: #fff;
  background: #b91908;
  cursor: pointer;
}

.playback-notice button:focus-visible {
  outline: 3px solid #ffbf75;
  outline-offset: 3px;
}

/* 视频播放器定位 */
.video-container .video-player {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.video-container .video-player.video-hidden {
  opacity: 0;
}

/* 骨架屏样式 */
.video-skeleton {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  /* 让点击穿透到下方的视频播放按钮，避免 iOS 老设备因骨架屏遮挡而无法点击播放 */
  pointer-events: none;
}

.skeleton-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.05) 50%,
    transparent 100%
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.skeleton-play-btn {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(213, 25, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.skeleton-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #d51900;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.skeleton-text {
  margin-top: 16px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 1px;
}

/* 自定义播放按钮覆盖层：布局与 .video-skeleton 完全一致，确保按钮位置与加载圆圈一致 */
.custom-play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 11;
  cursor: pointer;
  background: transparent;
}

/* 圆形按钮：在视频正中显示 */
.custom-play-btn {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(213, 25, 0, 0.9);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: background 0.2s ease, transform 0.15s ease;
}

.custom-play-overlay:active .custom-play-btn {
  background: #d51900;
  transform: scale(0.95);
}

/* SVG 三角图标：SVG path 本身已做光学居中（几何中心略偏右补偿视觉重心），不再额外偏移 */
.custom-play-icon {
  width: 30px;
  height: 30px;
  display: block;
}

/* 无视频占位样式 */
.video-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.placeholder-icon {
  width: 64px;
  height: 64px;
  color: #ccc;
}

.placeholder-icon svg {
  width: 100%;
  height: 100%;
}

.placeholder-text {
  margin-top: 12px;
  font-size: 14px;
  color: #999;
}

/* PDF容器 */
.pdf-container {
  background: #ffffff;
}

.pdf-iframe {
  width: 100%;
  height: 400px;
  border: none;
  -webkit-overflow-scrolling: touch;
}

/* 课程信息卡片 */
.course-info-card {
  background: #ffffff;
  margin: 16px;
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.course-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.course-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a2a3a;
  margin: 0;
  line-height: 1.4;
}

/* 标题骨架屏 */
.title-skeleton {
  width: 100%;
}

.skeleton-line {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-title {
  height: 22px;
  width: 70%;
}

/* 投票按钮骨架屏 */
.vote-buttons-skeleton {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}

.skeleton-btn {
  width: 60px;
  height: 32px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 16px;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* 投票按钮 */
.vote-buttons {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}

.vote-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 16px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.vote-btn:active {
  transform: scale(0.95);
}

.vote-btn.active {
  background: #d51900;
  color: #fff;
}

.vote-btn.active:first-child {
  background: #d51900;
}

.vote-btn.active:last-child {
  background: #666;
}

.vote-btn svg {
  width: 16px;
  height: 16px;
}

.vote-count {
  font-size: 13px;
  font-weight: 500;
}

.course-desc {
  font-size: 14px;
  color: #5a6a7a;
  margin: 0;
  line-height: 1.6;
}

/* 考试入口卡片 */
.exam-entry-card {
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding: 12px 14px;
  background: linear-gradient(135deg, #fff5f5, #fff0f0);
  border: 1px solid #ffd6d6;
  border-radius: 10px;
  cursor: pointer;
}
.exam-entry-card:active {
  opacity: 0.8;
}
.exam-card-icon {
  font-size: 28px;
  margin-right: 12px;
  flex-shrink: 0;
}
.exam-card-info {
  flex: 1;
  min-width: 0;
}
.exam-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #d51900;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.exam-card-desc {
  font-size: 12px;
  color: #999;
}
.exam-card-arrow {
  font-size: 22px;
  color: #d51900;
  font-weight: bold;
  margin-left: 8px;
  flex-shrink: 0;
}

/* 评论区域 */
.comment-section {
  background: #ffffff;
  margin: 0 16px 80px; /* 下方留出浮窗按钮空间 */
  border-radius: 12px;
  overflow: hidden;
}

/* 评论骨架屏 */
.comment-skeleton {
  padding: 16px;
}

.skeleton-comment-item {
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;
}

.skeleton-comment-item:last-child {
  border-bottom: none;
}

.skeleton-comment-meta {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.skeleton-floor {
  width: 30px;
  height: 14px;
}

.skeleton-author {
  width: 60px;
  height: 14px;
}

.skeleton-content {
  width: 100%;
  height: 16px;
  margin-bottom: 8px;
}

.skeleton-content-short {
  width: 60%;
  height: 16px;
}

/* 评论未开启 */
.comment-closed {
  padding: 40px 16px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.comment-closed p {
  margin: 0;
}

/* 评论列表头部 */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #eee;
  background: #fff;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.comment-count {
  font-size: 13px;
  color: #999;
}

/* 评论列表 */
.comment-list {
  background: #fff;
}

.comment-item {
  border-bottom: 1px solid #f0f0f0;
}

.comment-item:last-child {
  border-bottom: none;
}

.comment-main {
  padding: 14px 16px;
}

.comment-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.comment-floor {
  font-size: 12px;
  color: #999;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
}

.comment-author {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.comment-body {
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  word-break: break-word;
}

.comment-footer {
  margin-top: 10px;
}

.reply-btn {
  font-size: 13px;
  color: #d51900;
  cursor: pointer;
}

.reply-btn:active {
  opacity: 0.7;
}

/* 回复列表 */
.reply-list {
  background: #f8f9fa;
  padding: 0 16px 0 32px;
}

.reply-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
  font-size: 13px;
}

.reply-item:last-child {
  border-bottom: none;
}

.reply-author {
  color: #d51900;
  font-weight: 500;
  flex-shrink: 0;
}

.reply-author::after {
  content: '：';
}

.reply-content {
  flex: 1;
  color: #333;
  line-height: 1.5;
  word-break: break-word;
}

.reply-action {
  color: #999;
  font-size: 12px;
  flex-shrink: 0;
  cursor: pointer;
}

.reply-action:active {
  color: #d51900;
}

/* 空评论状态 */
.empty-comment {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 14px;
}

.empty-comment p {
  margin: 0;
}

/* ========== 右下角浮窗按钮 ========== */
.comment-fab {
  position: fixed;
  right: 20px;
  bottom: calc(20px + env(safe-area-inset-bottom));
  width: 56px;
  height: 56px;
  background: #d51900;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(213, 25, 0, 0.4);
  z-index: 99;
  cursor: pointer;
}

.comment-fab:active {
  opacity: 0.85;
}

.comment-fab svg {
  width: 26px;
  height: 26px;
  color: #fff;
}

.fab-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  background: #ff6b4a;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
}

/* ========== 评论弹窗 ========== */
.comment-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 200;
}

.comment-panel {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  border-radius: 16px 16px 0 0;
  z-index: 201;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  padding-bottom: env(safe-area-inset-bottom);
  transform: translateY(0);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.panel-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.panel-close {
  font-size: 24px;
  color: #999;
  cursor: pointer;
  line-height: 1;
}

.panel-close:active {
  color: #666;
}

.panel-body {
  padding: 16px 20px;
  flex: 1;
}

.panel-textarea {
  background: #f8f9fa !important;
  border: 1px solid #e8e8e8 !important;
  border-radius: 8px !important;
  padding: 12px !important;
  font-size: 15px !important;
  min-height: 100px !important;
}

.panel-textarea:focus-within {
  border-color: #d51900 !important;
  background: #fff !important;
}

.panel-footer {
  display: flex;
  gap: 12px;
  padding: 12px 20px 16px;
  border-top: 1px solid #f0f0f0;
}

.panel-cancel {
  flex: 1;
  padding: 12px;
  background: #f5f5f5;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  color: #666;
  cursor: pointer;
}

.panel-cancel:active {
  background: #e8e8e8;
}

.panel-submit {
  flex: 1;
  padding: 12px;
  background: #d51900;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
}

.panel-submit:active {
  opacity: 0.9;
}

/* ========== 动画 ========== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active {
  transition: transform 0.3s ease-out;
}

.slide-up-leave-active {
  transition: transform 0.25s ease-in;
}

.slide-up-enter {
  transform: translateY(100%);
}

.slide-up-leave-to {
  transform: translateY(100%);
}

/* 确保弹窗组件动画不影响页面 */
.comment-overlay,
.comment-panel {
  will-change: transform, opacity;
}

/* 考试入口弹窗样式 */
.exam-entry-content {
  padding: 20px;
  text-align: center;
}

.exam-entry-text {
  font-size: 16px;
  color: #333;
  margin: 0;
}
</style>

<style>
/* 视频播放器样式覆盖 */
.study-page .video-container .video-js {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  border-radius: 0;
}

.study-page .video-js .vjs-tech {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 隐藏 video.js 默认大播放按钮，使用自定义 SVG 播放按钮代替 */
.study-page .video-js .vjs-big-play-button {
  display: none !important;
}

/* 确保 video.js 容器不影响布局 */
.study-page .video-player-box {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
}

/* 控制条样式优化 */
.study-page .video-js .vjs-control-bar {
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7)) !important;
}

.study-page .video-js .vjs-play-progress,
.study-page .video-js .vjs-volume-level {
  background: #d51900 !important;
}

/* 加载动画样式 */
.study-page .video-js .vjs-loading-spinner {
  border-color: rgba(213, 25, 0, 0.8) !important;
}

.study-page .video-js .vjs-loading-spinner:before,
.study-page .video-js .vjs-loading-spinner:after {
  border-top-color: #d51900 !important;
}
</style>



// WEBPACK FOOTER //
// src/views/classStudy.vue